import type { SessionUser } from "./session";

const EM_DASH_OR_HYPHEN = /[—\-]/g;

function stripDashPlaceholders(value: string): string {
  return value.replace(EM_DASH_OR_HYPHEN, "").trim();
}

function initialsFromSingleFullNameToken(token: string): string {
  return token.length >= 2
    ? token.slice(0, 2).toUpperCase()
    : `${token[0]!}${token[0]!}`.toUpperCase();
}

export type UserProfileDisplay = {
  /** Session / external id — used for initials when name and email are missing */
  userId: string;
  email: string;
  companySummaryName: string;
  firstName: string;
  lastName: string;
  /** From SSO `FullName` when first/last are empty */
  fullName: string;
  company: string;
  nmlsId: string;
};

/** Two-letter initials for avatar (first + last name, else email local part). */
export function getUserInitials(profile: UserProfileDisplay): string {
  const f = stripDashPlaceholders(profile.firstName);
  const l = stripDashPlaceholders(profile.lastName);
  if (f && l) return `${f[0]!}${l[0]!}`.toUpperCase();
  if (f.length >= 2) return f.slice(0, 2).toUpperCase();
  if (f.length === 1) return `${f[0]!}${f[0]!}`.toUpperCase();
  const fnFull = stripDashPlaceholders(profile.fullName);
  if (fnFull) {
    const parts = fnFull.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]![0]!}${parts[parts.length - 1]![0]!}`.toUpperCase();
    }
    /* v8 ignore start — non-empty fnFull ⇒ at least one token after split+filter(Boolean) */
    if (parts.length === 1) {
      return initialsFromSingleFullNameToken(parts[0]!);
    }
    /* v8 ignore end */
  }
  const email = profile.email.trim();
  if (email && email !== "—") {
    const local = email.split("@")[0] || "";
    const alnum = local.replace(/[^a-zA-Z0-9]/g, "");
    if (alnum.length >= 2) return alnum.slice(0, 2).toUpperCase();
    if (alnum.length === 1) return `${alnum[0]!}${alnum[0]!}`.toUpperCase();
  }
  const uid = profile.userId.trim();
  if (uid) {
    const letters = uid.replace(/[^a-zA-Z]/g, "");
    if (letters.length >= 2) return letters.slice(0, 2).toUpperCase();
    if (letters.length === 1) return `${letters[0]!}${letters[0]!}`.toUpperCase();
    const digits = uid.replace(/\D/g, "");
    if (digits.length >= 2) return digits.slice(-2).toUpperCase();
    if (uid.length >= 2) return uid.slice(0, 2).toUpperCase();
  }
  return "??";
}

export function getFullDisplayName(profile: UserProfileDisplay): string {
  const fn = profile.fullName === "—" ? "" : profile.fullName.trim();
  if (fn) return fn;
  const f = profile.firstName === "—" ? "" : profile.firstName.trim();
  const l = profile.lastName === "—" ? "" : profile.lastName.trim();
  const combined = [f, l].filter(Boolean).join(" ");
  if (combined) return combined;
  const e = profile.email.trim();
  if (e && e !== "—") return e;
  return profile.userId.trim() || "User";
}

/** Build header + modal fields from `/api/me` user payload and optional cookie fallbacks. */
export function sessionUserToProfile(
  user: SessionUser,
  companyIdCookie?: string | null,
): UserProfileDisplay {
  const email = user.email?.trim() || "—";
  const companyFromUser =
    user.company?.trim() || user.companyName?.trim() || undefined;
  const companyIdFromSession = user.companyId?.trim();
  const company =
    companyFromUser ??
    (companyIdFromSession ? `Company ${companyIdFromSession}` : undefined) ??
    (companyIdCookie?.trim() ? `Company ${companyIdCookie.trim()}` : "—");
  const companySummaryName =
    companyFromUser ?? companyIdFromSession ?? companyIdCookie?.trim() ?? "—";
  const fullName = user.fullName?.trim() || "—";
  return {
    userId: user.userId,
    email,
    companySummaryName,
    firstName: user.firstName?.trim() || "—",
    lastName: user.lastName?.trim() || "—",
    fullName,
    company,
    nmlsId: user.nmlsId?.trim() || "—",
  };
}
